/**
 * RichTextEditor Component
 * Wrapper around React-Quill for consistent rich text editing across the site
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styled from 'styled-components';

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = 'Enter text...', 
  height = '200px',
  readOnly = false 
}) => {
  // Configure toolbar modules
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],  // Text size
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],          // Text color and background
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      [{ 'align': [] }],
      ['clean']
    ]
  }), []);

  // Configure formats - memoize to prevent recreation on every render
  const formats = useMemo(() => [
    'header',
    'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'link',
    'align'
  ], []);

  return (
    <EditorContainer $height={height}>
      <ReactQuill
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly}
      />
    </EditorContainer>
  );
};

const EditorContainer = styled.div`
  .quill {
    background: var(--bg-primary);
    border-radius: 0.375rem;
    border: 1px solid var(--border-color);
  }

  .ql-toolbar {
    background: var(--bg-secondary);
    border-top-left-radius: 0.375rem;
    border-top-right-radius: 0.375rem;
    border: 1px solid var(--border-color);
    border-bottom: none;
  }

  .ql-container {
    border-bottom-left-radius: 0.375rem;
    border-bottom-right-radius: 0.375rem;
    border: 1px solid var(--border-color);
    border-top: none;
    font-size: 1rem;
    font-family: inherit;
  }

  .ql-editor {
    min-height: ${props => props.$height};
    color: var(--text-primary);
    
    &::before {
      color: var(--text-secondary);
      font-style: normal;
    }
  }

  .ql-snow .ql-stroke {
    stroke: var(--text-secondary);
  }

  .ql-snow .ql-fill {
    fill: var(--text-secondary);
  }

  .ql-snow .ql-picker-label {
    color: var(--text-secondary);
  }

  .ql-toolbar button:hover,
  .ql-toolbar button:focus,
  .ql-toolbar button.ql-active,
  .ql-toolbar .ql-picker-label:hover,
  .ql-toolbar .ql-picker-label.ql-active,
  .ql-toolbar .ql-picker-item:hover,
  .ql-toolbar .ql-picker-item.ql-selected {
    color: var(--accent-primary);
  }

  .ql-snow .ql-picker-options {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
  }

  .ql-snow.ql-toolbar button:hover .ql-stroke,
  .ql-snow .ql-toolbar button:hover .ql-stroke,
  .ql-snow.ql-toolbar button:focus .ql-stroke,
  .ql-snow .ql-toolbar button:focus .ql-stroke,
  .ql-snow.ql-toolbar button.ql-active .ql-stroke,
  .ql-snow .ql-toolbar button.ql-active .ql-stroke {
    stroke: var(--accent-primary);
  }

  .ql-snow.ql-toolbar button:hover .ql-fill,
  .ql-snow .ql-toolbar button:hover .ql-fill,
  .ql-snow.ql-toolbar button:focus .ql-fill,
  .ql-snow .ql-toolbar button:focus .ql-fill,
  .ql-snow.ql-toolbar button.ql-active .ql-fill,
  .ql-snow .ql-toolbar button.ql-active .ql-fill {
    fill: var(--accent-primary);
  }

  /* Links styling */
  .ql-editor a {
    color: var(--accent-primary);
    text-decoration: underline;
  }

  /* Lists styling */
  .ql-editor ul,
  .ql-editor ol {
    padding-left: 1.5em;
  }

  /* Headers styling */
  .ql-editor h1 {
    font-size: 2em;
    font-weight: bold;
  }

  .ql-editor h2 {
    font-size: 1.5em;
    font-weight: bold;
  }

  .ql-editor h3 {
    font-size: 1.17em;
    font-weight: bold;
  }

  /* Size classes - Quill uses these for text sizing */
  .ql-editor .ql-size-small {
    font-size: 0.75em;
  }

  .ql-editor .ql-size-large {
    font-size: 1.5em;
  }

  .ql-editor .ql-size-huge {
    font-size: 2.5em;
  }
`;

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  height: PropTypes.string,
  readOnly: PropTypes.bool
};

RichTextEditor.defaultProps = {
  value: '',
  placeholder: 'Enter text...',
  height: '200px',
  readOnly: false
};

// Memoize RichTextEditor to prevent unnecessary re-renders
export default React.memo(RichTextEditor);
